import { useState } from "react";

const PEAK_TOOLTIPS: Record<string, string> = {
  "Morning Peak": "Block range: 12–20",
  "Solar Peak": "Block range: 21–36",
  "Evening Peak": "Block range: 37–45",
};

function InfoTooltip({ text }: { text: string }) {
  const [visible, setVisible] = useState(false);
  return (
    <span
      className="relative inline-flex items-center"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#8C8A96"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="cursor-default shrink-0"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      {visible && (
        <span className="absolute z-[9999] bottom-full left-0 mb-1.5 whitespace-nowrap rounded-lg border border-[#DFEAF2] bg-white px-2.5 py-1.5 text-[10px] font-medium text-[#3D6B74] shadow-lg pointer-events-none">
          {text}
        </span>
      )}
    </span>
  );
}

type WeatherStats = {
  temp?: number | null;
  humid?: number | null;
  solar?: number | null;
};

type PeakCardProps = {
  title: string;
  peakBlock: number | null;
  peakDate?: string | null;
  peakValue: number | null;
  peakPrevDay?: number | null;
  unit: string;
  weather?: WeatherStats | null;
  weatherPrevDay?: WeatherStats | null;
  showPrevDay?: boolean;
};

function formatDate(iso: string) {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function fmt(v: number | null | undefined): string {
  if (v == null) return "—";
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

const WeatherIcon = ({ type }: { type: "temp" | "humid" | "solar" }) => {
  if (type === "temp")
    return (
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z" />
      </svg>
    );
  if (type === "humid")
    return (
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
      </svg>
    );
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2" x2="12" y2="4" />
      <line x1="12" y1="20" x2="12" y2="22" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="2" y1="12" x2="4" y2="12" />
      <line x1="20" y1="12" x2="22" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
};

const WEATHER_ITEMS: {
  label: string;
  iconType: "temp" | "humid" | "solar";
  key: keyof WeatherStats;
  unit: string;
}[] = [
  { label: "Temperature", iconType: "temp", key: "temp", unit: "°C" },
  { label: "Humidity", iconType: "humid", key: "humid", unit: "%" },
  { label: "Solar Irrad.", iconType: "solar", key: "solar", unit: "W/m²" },
];

export function PeakCard({
  title,
  peakBlock,
  peakDate,
  peakValue,
  peakPrevDay,
  unit,
  weather,
  weatherPrevDay,
  showPrevDay = false,
}: PeakCardProps) {
  return (
    <div className="bg-white rounded-xl border border-[#DFEAF2] p-3 flex flex-col gap-1">
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
          {PEAK_TOOLTIPS[title] && <InfoTooltip text={PEAK_TOOLTIPS[title]} />}
          <span className="text-xs font-medium text-[#14171F] whitespace-nowrap">
            {title}
          </span>
          {peakDate && (
            <span className="text-[8px] font-semibold text-[#34656D] bg-[#E8F4F8] pl-1.5 py-0.5 rounded-md flex items-center gap-1">
              {formatDate(peakDate)}
              {peakBlock != null && (
                <span className="text-[8px] font-medium text-white bg-[#3D6B74] px-1 py-px rounded">
                  {peakBlock}
                </span>
              )}
            </span>
          )}
        </div>
        <div className="flex flex-col items-end shrink-0">
          <p className="text-lg font-medium text-[#14171F] whitespace-nowrap">
            {peakValue == null ? (
              <span className="text-[#A3AED0]">—</span>
            ) : (
              <>
                {peakValue.toLocaleString("en-IN")}{" "}
                <span className="text-xs font-normal text-[#6B7280]">
                  {unit}
                </span>
              </>
            )}
          </p>
          {showPrevDay &&
            peakPrevDay != null &&
            peakValue != null &&
            (() => {
              const diff = peakPrevDay - peakValue;
              const color =
                diff > 0 ? "#22c55e" : diff < 0 ? "#ef4444" : "#8C8A96";
              const arrow = diff > 0 ? "▲" : diff < 0 ? "▼" : "";
              return (
                <p
                  className="text-[10px] font-medium leading-tight flex items-center gap-0.5"
                  style={{ color }}
                >
                  {arrow && <span className="text-[8px]">{arrow}</span>}
                  {peakPrevDay.toLocaleString("en-IN")}{" "}
                  <span className="text-[9px] font-normal text-[#8C8A96]">
                    prev day
                  </span>
                </p>
              );
            })()}
        </div>
      </div>
      <hr className="border-t border-[#DFEAF2]" />

      {/* Weather stats */}
      <div className="flex justify-between mt-1 divide-x divide-[#DFEAF2]">
        {WEATHER_ITEMS.map((item, idx) => {
          const val = weather?.[item.key];
          const prev = weatherPrevDay?.[item.key];
          return (
            <div
              key={item.key}
              className={`flex flex-col gap-0.5 ${idx > 0 ? "pl-3" : ""}`}
            >
              <p className="text-[9px] text-[#8C8A96] flex items-center gap-1">
                {item.label}
                <span
                  style={{
                    color:
                      item.iconType === "temp"
                        ? "#f97316"
                        : item.iconType === "humid"
                          ? "#3b82f6"
                          : "#eab308",
                  }}
                >
                  <WeatherIcon type={item.iconType} />
                </span>
              </p>
              <p className="text-[14px] font-medium text-[#14171F] leading-tight whitespace-nowrap">
                {fmt(val)}
                <span className="text-[10px] font-normal text-[#6B7280] ml-0.5">
                  {item.unit}
                </span>
              </p>
              {showPrevDay &&
                val != null &&
                prev != null &&
                (() => {
                  const diff = prev - val;
                  const color =
                    diff > 0 ? "#22c55e" : diff < 0 ? "#ef4444" : "#8C8A96";
                  const arrow = diff > 0 ? "▲" : diff < 0 ? "▼" : "";
                  return (
                    <p
                      className="text-[10px] font-medium leading-tight flex items-center gap-0.5"
                      style={{ color }}
                    >
                      {arrow && <span className="text-[8px]">{arrow}</span>}
                      {fmt(prev)}{" "}
                      <span className="text-[9px] font-normal text-[#8C8A96]">
                        prev day
                      </span>
                    </p>
                  );
                })()}
            </div>
          );
        })}
      </div>
    </div>
  );
}
